"use client"

import { useEffect, useId, useMemo, useState } from "react"
import { useField, useFormikContext } from "formik"
import { AlertCircle, Loader2 } from "lucide-react"

import { HandoverPipelineSelector } from "@/components/agents/email/handover-pipeline-selector"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useActiveCrmIntegrationsQuery,
  useCrmPipelinesQuery,
} from "@/hooks/queries/crm-integrations"
import type { CrmPipeline } from "@/lib/crm/types"

export function CrmReadyToShipStageField({
  disabled = false,
  scope = "tenant_default",
  name = "crmStageId",
  label = "CRM ready-to-ship stage (Optional)",
  description = "After a waybill is created, move the CRM order to this worker stage.",
}: {
  disabled?: boolean
  scope?: "tenant_default" | "agent"
  name?: string
  label?: string
  description?: string
}) {
  const [field, meta] = useField<string>(name)
  const { setFieldTouched, setFieldValue } = useFormikContext()
  const integrationsQuery = useActiveCrmIntegrationsQuery()
  const integrations = useMemo(
    () => integrationsQuery.data ?? [],
    [integrationsQuery.data],
  )
  const [integrationId, setIntegrationId] = useState("")
  const pipelinesQuery = useCrmPipelinesQuery(integrationId)
  const pipelines = useMemo(
    () => pipelinesQuery.data ?? [],
    [pipelinesQuery.data],
  )
  const [pipelineId, setPipelineId] = useState("")
  const stages = useMemo(
    () => findPipeline(pipelines, pipelineId)?.stages ?? [],
    [pipelineId, pipelines],
  )
  const error = meta.touched && meta.error ? meta.error : null

  useEffect(() => {
    if (integrations.length === 0) {
      setIntegrationId("")
      return
    }
    if (integrations.some(integration => integration.id === integrationId)) {
      return
    }
    setIntegrationId(integrations[0].id)
  }, [integrationId, integrations])

  useEffect(() => {
    if (pipelines.length === 0) {
      setPipelineId("")
      return
    }
    const stagePipeline = findPipelineForStage(pipelines, field.value)
    if (stagePipeline) {
      setPipelineId(stagePipeline.id)
      return
    }
    if (findPipeline(pipelines, pipelineId)) return
    setPipelineId(findInitialPipelineId(pipelines))
  }, [field.value, pipelineId, pipelines])

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="space-y-1">
        <Label>{label}</Label>
        <p className="text-xs text-muted-foreground">
          {description}
        </p>
      </div>
      {renderPickerState({
        error,
        fieldValue: field.value,
        integrationId,
        integrations,
        integrationsError: integrationsQuery.error,
        integrationsLoading: integrationsQuery.isLoading,
        disabled,
        onIntegrationChange: nextIntegrationId => {
          if (!nextIntegrationId || nextIntegrationId === integrationId) return
          setIntegrationId(nextIntegrationId)
          setPipelineId("")
          void setFieldValue(name, "")
          void setFieldTouched(name, true, false)
        },
        onPipelineChange: nextPipelineId => {
          // Select can emit its controlled value while async options mount.
          // Only a real pipeline change should clear the saved stage.
          if (!nextPipelineId || nextPipelineId === pipelineId) return
          setPipelineId(nextPipelineId)
          void setFieldValue(name, "")
          void setFieldTouched(name, true, false)
        },
        onStageChange: stageId => {
          if (!stageId || stageId === field.value) return
          void setFieldValue(name, stageId)
          void setFieldTouched(name, true, false)
        },
        pipelineId,
        pipelines,
        pipelinesError: pipelinesQuery.error,
        pipelinesLoading: pipelinesQuery.isLoading,
        stages,
        scope,
      })}
    </div>
  )
}

type PickerStateInput = {
  disabled: boolean
  error: string | null
  fieldValue: string
  integrationId: string
  integrations: Array<{ id: string; name: string }>
  integrationsError: Error | null
  integrationsLoading: boolean
  onIntegrationChange: (integrationId: string) => void
  onPipelineChange: (pipelineId: string) => void
  onStageChange: (stageId: string) => void
  pipelineId: string
  pipelines: CrmPipeline[]
  pipelinesError: Error | null
  pipelinesLoading: boolean
  stages: CrmPipeline["stages"]
  scope: "tenant_default" | "agent"
}

function renderPickerState(input: PickerStateInput) {
  if (input.integrationsLoading) {
    return <LoadingStatus message="Loading CRM integrations…" />
  }
  if (input.integrationsError) {
    return <ErrorStatus message={input.integrationsError.message} />
  }
  if (input.integrations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Connect and activate a CRM integration to select a ready-to-ship stage.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {renderMultipleCrmNotice(input)}
      {renderPipelineState(input)}
      {input.error ? (
        <p className="text-xs font-medium text-destructive">{input.error}</p>
      ) : null}
    </div>
  )
}

function renderMultipleCrmNotice(input: PickerStateInput) {
  if (input.integrations.length <= 1) return null
  return (
    <div className="space-y-2">
      <CrmIntegrationSelect
        disabled={input.disabled}
        integrationId={input.integrationId}
        integrations={input.integrations}
        onChange={input.onIntegrationChange}
      />
      <p className="text-xs text-amber-700 dark:text-amber-400">
        {getMultipleCrmNotice(input.scope)}
      </p>
    </div>
  )
}

function renderPipelineState(input: PickerStateInput) {
  if (!input.integrationId || input.pipelinesLoading) {
    return <LoadingStatus message="Loading CRM pipelines and stages…" />
  }
  if (input.pipelinesError) {
    return <ErrorStatus message={input.pipelinesError.message} />
  }
  return (
    <HandoverPipelineSelector
      compact
      pipelines={input.pipelines}
      stages={input.stages}
      selectedPipelineId={input.pipelineId}
      selectedStageId={input.fieldValue}
      isSaving={input.disabled}
      onPipelineChange={input.onPipelineChange}
      onStageChange={input.onStageChange}
    />
  )
}

function CrmIntegrationSelect({
  disabled,
  integrationId,
  integrations,
  onChange,
}: {
  disabled: boolean
  integrationId: string
  integrations: Array<{ id: string; name: string }>
  onChange: (integrationId: string) => void
}) {
  const selectId = useId()
  return (
    <div className="space-y-1.5">
      <Label htmlFor={selectId}>CRM integration</Label>
      <Select value={integrationId} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={selectId}>
          <SelectValue placeholder="Select a CRM integration" />
        </SelectTrigger>
        <SelectContent>
          {integrations.map(integration => (
            <SelectItem key={integration.id} value={integration.id}>
              {integration.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function getMultipleCrmNotice(scope: "tenant_default" | "agent"): string {
  switch (scope) {
    case "tenant_default":
      return "This tenant default stores only the stage ID. If agents use CRMs with different stages, configure their delivery stage individually."
    case "agent":
      return "This agent setting stores only the stage ID. Select the stage from the CRM this agent uses."
    default: {
      const exhaustiveScope: never = scope
      return exhaustiveScope
    }
  }
}

function LoadingStatus({ message }: { message: string }) {
  return (
    <div
      className="flex items-center gap-2 text-sm text-muted-foreground"
      aria-live="polite"
    >
      <Loader2
        className="h-4 w-4 animate-spin motion-reduce:animate-none"
        aria-hidden="true"
      />
      {message}
    </div>
  )
}

function ErrorStatus({ message }: { message: string }) {
  return (
    <div
      className="flex items-start gap-2 text-sm text-destructive"
      role="alert"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      {message}
    </div>
  )
}

function findPipeline(
  pipelines: CrmPipeline[],
  pipelineId: string,
): CrmPipeline | undefined {
  return pipelines.find(pipeline => pipeline.id === pipelineId)
}

function findPipelineForStage(
  pipelines: CrmPipeline[],
  stageId: string,
): CrmPipeline | undefined {
  if (!stageId) return undefined
  return pipelines.find(pipeline => (
    pipeline.stages.some(stage => stage.id === stageId)
  ))
}

function findInitialPipelineId(pipelines: CrmPipeline[]): string {
  return pipelines.find(pipeline => pipeline.isDefault)?.id
    ?? pipelines[0]?.id
    ?? ""
}
